import * as datefns from "date-fns";
import addDuration from "date-fns-duration";

import { BookingRefundRule, BookingRefundRules } from "../../types/booking";
import { sortByDuration, sortByKey } from "../sort";

/**
 * Find the active refund rules based on date time when booking has been created
 * @param bookingTimeCreated
 * @param cancellationPolicies
 * @returns BookingRefundRules
 */
export const getBookingRefundRules = (
  bookingTimeCreated: Date,
  cancellationPolicies: BookingRefundRules[],
): BookingRefundRules => {
  const policies = sortByKey(cancellationPolicies, "startDateTime").reverse();
  const rules = policies.find(currRules =>
    datefns.isAfter(bookingTimeCreated, datefns.parseISO(currRules.startDateTime)),
  );

  if (typeof rules === "undefined") {
    throw new Error("No refund cancellation rules are found.");
  }

  return rules;
};

/**
 * Return refund rule which should be applied during the cancellation process.
 * @param timeCreated
 * @param timeStarts
 * @param cancellationPolicies
 * @returns BookingRefundRule
 */
export const getRefundRule = (
  timeCreated: Date,
  timeStarts: Date,
  cancellationPolicies: BookingRefundRules[],
): BookingRefundRule => {
  const now = Date.now();

  const refundRules = getBookingRefundRules(timeCreated, cancellationPolicies);

  if (datefns.isBefore(now, addDuration(timeCreated, refundRules.fullRefundMaxMinutes.duration))) {
    return refundRules.fullRefundMaxMinutes;
  }

  const sortedIntervals = sortByDuration(refundRules.intervals, "duration");
  const matchedRule = sortedIntervals.find(rule =>
    datefns.isAfter(now, addDuration(timeStarts, `-${rule.duration}`)),
  );

  if (typeof matchedRule === "undefined") {
    throw Error("No cancellation rule is found.");
  }

  return matchedRule;
};
